import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { isUndefined, some } from '@sama/micro-dash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { findKeyLocationIndex } from '../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import {
  midPoint,
  reducePoints,
  subtractPoints,
} from '../../../../shared/misc-utilities/point-utils';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { ExtrapolationService } from '../../utils/extrapolation/extrapolation.service';
import { AbstractShapeComponent } from '../abstract-shape.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- svg needs attribute selectors
  selector: '[h3Rectangle]',
  templateUrl: './rectangle.component.html',
  styleUrls: ['../../shape-parts/outline-host.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RectangleComponent
  extends AbstractShapeComponent
  implements OnInit
{
  center$!: Observable<Point2D>;
  centerText$?: Observable<string>;
  mlIsLoading$!: Observable<boolean>;
  private extrapolationService = inject(ExtrapolationService);

  override ngOnInit() {
    super.ngOnInit();
    this.center$ = this.points$.pipe(map(midPoint));
    this.centerText$ = this.buildCenterText$();
    this.mlIsLoading$ = this.buildMlIsLoading$();
  }

  protected override buildAllSubpointInfo$() {
    return combineLatest([
      super.buildAllSubpointInfo$(),
      this.store('editMode').$.pipe(distinctUntilChanged()),
    ]).pipe(
      map(([allBaseSubpointInfo, editMode]) => {
        if (editMode === 'drag') {
          return allBaseSubpointInfo.map((baseSubpointInfo) => {
            if (baseSubpointInfo.subpointIndex % 2) {
              return { ...baseSubpointInfo, cursor: 'nesw-resize' };
            } else {
              return { ...baseSubpointInfo, cursor: 'nwse-resize' };
            }
          });
        } else {
          return allBaseSubpointInfo;
        }
      }),
    );
  }

  private buildCenterText$() {
    return combineLatest([
      this.shapeStore('showDimensions').$,
      this.points$,
      this.store('layerSettings')('showShapeDimensions').$,
    ]).pipe(
      map(([showDimensions, points, showShapeDimensions]) => {
        if (isUndefined(showDimensions)) {
          // fix me if you change this code
          // eslint-disable-next-line no-param-reassign -- fix me!
          showDimensions = showShapeDimensions;
        }
        if (showDimensions && points.length) {
          const min = reducePoints(points, Math.min);
          const max = reducePoints(points, Math.max);
          const dimensions = subtractPoints(max, min);
          return dimensions
            .map(
              (dimension) => Math.round(dimension * 100 + Number.EPSILON) / 100,
            )
            .join(' x ');
        } else {
          return '';
        }
      }),
    );
  }

  private buildMlIsLoading$() {
    return combineLatest([
      this.shapeStore('index').$.pipe(
        switchMap((index) => this.getInFlightRequests$(index)),
      ),
      this.store('frameNumber').$,
      this.shapeStore('key_locations').$,
    ]).pipe(
      map(([inFlightRequests, frameNumber, keyLocations]) =>
        some(inFlightRequests, (request) => {
          if (frameNumber <= request.requestOptions.startKeyFrame) {
            return false;
          }

          // ml extrapolation requests only affect frames preceding the next
          // keyframe
          const nextKeyframe =
            keyLocations[
              findKeyLocationIndex(
                keyLocations,
                request.requestOptions.startKeyFrame,
              ) + 1
            ];
          // fix me if you change this code
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
          if (nextKeyframe) {
            return nextKeyframe.frame_number > frameNumber;
          }
          return true;
        }),
      ),
    );
  }

  private getInFlightRequests$(index: number) {
    return this.extrapolationService.inFlightRequests$.pipe(
      map((inFlightRequestSet) =>
        Array.from(inFlightRequestSet).filter(
          (request) => request.requestOptions.shapeIndex === index,
        ),
      ),
    );
  }
}
